import { toast } from "sonner";
import { CustomerPicker } from "../../../components/common/CustomerPicker";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { fmtDateTime } from "../../../lib/format";
import { useConfirm } from "../../../components/common/ConfirmDialog";

const roleOf = (u) => (u.roles.includes("customer_admin") ? "approver" : "buyer");

export const UsersTable = ({ users, customersByCode, customers, onChanged, onLink }) => {
  const { t } = useI18n();
  const confirm = useConfirm();
  const patch = async (u, body) => {
    try {
      await api.patch(`/admin/users/${u.id}`, body);
      toast.success(t("users.saved"));
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    }
  };
  const resend = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/resend-invite`);
      onLink(data.invite_link);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    }
    onChanged();
  };
  const relink = async (u, code) => {
    if (await confirm({ title: t("users.confirmRelink", { code }) })) patch(u, { customer_code: code });
  };
  const sel = "h-11 w-full min-w-0 rounded-xl border border-slate-200 bg-white px-2 text-xs text-navy";

  if (!users.length) return <p className="rounded-2xl bg-white px-5 py-8 text-center text-sm text-slate-500 shadow-sm">{t("users.empty")}</p>;
  return (
    <div className="space-y-3" data-testid="users-list">
      {users.map((u) => (
        <div key={u.id} data-testid={`user-row-${u.email}`} className={`grid min-w-0 gap-3 rounded-2xl bg-white p-4 shadow-sm md:grid-cols-[1.5fr_1.5fr_1fr_auto] md:items-center ${u.active ? "" : "opacity-60"}`}>
          <div className="min-w-0">
            <div className="truncate font-semibold text-navy">{u.name}</div>
            <div className="truncate text-xs text-slate-500">{u.email}</div>
            <div className="mt-1 flex gap-2 text-xs">
              <span className={`rounded-full px-2 py-0.5 font-semibold ${u.status === "invited" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`} data-testid={`user-status-${u.email}`}>{t(`users.status.${u.active ? u.status : "deactivated"}`)}</span>
              {u.last_login_at && <span className="text-slate-500">{fmtDateTime(u.last_login_at)}</span>}
            </div>
          </div>
          <CustomerPicker customers={customers} value={u.customer_code || ""} onChange={(code) => relink(u, code)} testId={`user-customer-${u.email}`} />
          <select value={roleOf(u)} onChange={(e) => patch(u, { role: e.target.value })} className={sel} data-testid={`user-role-${u.email}`}>
            <option value="approver">{t("users.roles.approver")}</option>
            <option value="buyer">{t("users.roles.buyer")}</option>
          </select>
          <div className="flex gap-2">
            {u.status === "invited" && u.active && (
              <button type="button" onClick={() => resend(u)} className="h-11 rounded-full border border-navy/15 px-3 text-xs font-semibold text-navy hover:bg-canvas" data-testid={`user-resend-${u.email}`}>{t("users.resend")}</button>
            )}
            <button type="button" onClick={() => patch(u, { active: !u.active })} data-testid={`user-toggle-${u.email}`}
              className={`h-11 rounded-full px-3 text-xs font-semibold ${u.active ? "bg-rose-50 text-rose-700 hover:bg-rose-100" : "bg-brand/10 text-brand hover:bg-brand/20"}`}>
              {u.active ? t("users.deactivate") : t("users.activate")}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};
